import {
  Stack,
  Typography,
  Card,
  CardContent,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Divider,
} from "@mui/material";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useServiceRequestByOrganization, filterServiceRequestsByTUSSProcedimentosEventos } from "@/services/fhir/servicerequest/hooks/useServiceRequestByOrganization";
import { useLocationContext } from "@/contexts/LocationContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from "recharts";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";
import { GenericTable } from "@/components/GenericTable/GenericTable";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map(item => item.label.length));
  const calculatedWidth = Math.max(150, (maxLabelLength * 7) + 20);
  return Math.min(calculatedWidth, 400);
};
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { useDict } from "@/utils/useDict";
import { NoData } from "@/components/common/NoData";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`service-request-tabpanel-${index}`}
      aria-labelledby={`service-request-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface ServiceRequestData {
  id: string;
  status: string;
  statusLabel?: string;
  code: string;
  display: string;
  patientId: string;
  patientName: string;
  practitionerName: string;
  locationName: string;
  authoredOn: string;
  priority: string;
  priorityLabel?: string;
  intent: string;
  categoryLabel?: string;
}

function formatDatePt(date?: string): string {
  if (!date) return "-";
  try {
    const d = new Date(date);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return date;
  }
}

function inferCategoryLabel(sr: any): string {
  // tenta usar sr.category[0].text ou coding
  const text = sr?.category?.[0]?.text?.toLowerCase();
  const code = sr?.category?.[0]?.coding?.[0]?.code?.toLowerCase();
  const display = sr?.category?.[0]?.coding?.[0]?.display?.toLowerCase();
  const joined = [text, code, display].filter(Boolean).join(" ");
  if (joined.includes("image") || joined.includes("imaging") || joined.includes("radiology") || joined.includes("imagem")) {
    return "Imagem";
  }
  if (joined.includes("lab") || joined.includes("laboratory") || joined.includes("laboratorio") || joined.includes("laboratorial") || joined.includes("procedure")) {
    return "Laboratorial";
  }
  // fallback pelo sistema TUSS: procedimentos podem ser ambos; manter "Laboratorial" por padrão
  return "Laboratorial";
}

export function ServiceRequestAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientServiceRequests, setPatientServiceRequests] = useState<ServiceRequestData[]>([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(new Map());
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [topSearch, setTopSearch] = useState<string>("");
  const [examSearch, setExamSearch] = useState<string>("");

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      console.log("Filtros de exames atualizados:", filters);
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  // Hook para buscar ServiceRequests
  const {
    data: serviceRequestData,
    loading: loadingServiceRequests,
    fetchServiceRequests,
  } = useServiceRequestByOrganization({
    organizationId: organizationId || "",
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    includePatient: true,
    includeEncounter: true,
  });

  // Processar dados dos ServiceRequests
  const {
    serviceRequestsByStatus,
    serviceRequestsByCode,
    serviceRequestsByPriority,
    serviceRequestsByCategory,
    totalServiceRequests,
    serviceRequestsList,
    tussServiceRequests,
    patientsSummaryRows,
  } = useMemo(() => {
    const statusMap = new Map<string, number>();
    const codeMap = new Map<string, number>();
    const priorityMap = new Map<string, number>();
    const categoryMap = new Map<string, number>();
    const serviceRequestsList: ServiceRequestData[] = [];
    const patientsMap = new Map<string, fhir5.Patient>();

    // Processar pacientes
    if (serviceRequestData?.patients) {
      serviceRequestData.patients.forEach((patient) => {
        if (patient.id) {
          patientsMap.set(patient.id, patient);
        }
      });
    }

    // Filtrar apenas ServiceRequests com TUSSProcedimentosEventos
    const filteredServiceRequests = filterServiceRequestsByTUSSProcedimentosEventos(
      serviceRequestData?.serviceRequests || []
    );

    // Mapa de tradução de status customizado
    const statusTranslation: Record<string, string> = {
      active: "Ativo",
      "on-hold": "Suspenso",
      revoked: "Cancelado",
      completed: "Concluído",
      "entered-in-error": "Erro",
      draft: "Solicitado",
      unknown: "Desconhecido",
    };

    filteredServiceRequests.forEach((sr) => {
      // Status
      const status = sr.status || "unknown";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);

      // Código/Procedimento - verificar diferentes estruturas
      let code = "unknown";
      let display = "Procedimento não especificado";
      
      if (sr.code?.concept?.coding?.[0]) {
        code = sr.code.concept.coding[0].code || "unknown";
        display = sr.code.concept.coding[0].display || "Procedimento não especificado";
      }
      
      codeMap.set(display, (codeMap.get(display) || 0) + 1);

      // Prioridade
      const priority = sr.priority || "routine";
      priorityMap.set(priority, (priorityMap.get(priority) || 0) + 1);

      // Dados para a lista
      const patient = patientsMap.get(sr.subject?.reference?.replace("Patient/", "") || "");
      const patientName = patient?.name?.[0]?.text || "Paciente não encontrado";

      // Nome do profissional - tentar extrair do requester ou contained
      let practitionerName = "Profissional não especificado";
      if (sr.requester?.display) {
        practitionerName = sr.requester.display;
      } else if (sr.contained) {
        const practitioner = sr.contained.find(c => c.resourceType === "Practitioner");
        if (practitioner?.name?.[0]?.text) {
          practitionerName = practitioner.name[0].text;
        }
      }

      const categoryLabel = inferCategoryLabel(sr);
      categoryMap.set(categoryLabel, (categoryMap.get(categoryLabel) || 0) + 1);

      const normalizedStatus = (status || "unknown").toLowerCase().replace(/[^a-z]/g, "");
      const normalizedPriority = (priority || "unknown").toLowerCase();

      serviceRequestsList.push({
        id: sr.id || "",
        status: status,
        statusLabel: useDict.serviceRequestStatus[status] || useDict.serviceRequestStatus[normalizedStatus] || status || "Desconhecido",
        code: code,
        display: display,
        patientId: sr.subject?.reference?.replace("Patient/", "") || "",
        patientName: patientName,
        practitionerName: practitionerName,
        locationName: "Local não especificado", // Seria necessário buscar do encounter
        authoredOn: formatDatePt(sr.authoredOn || ""),
        priority: priority,
        priorityLabel: useDict.serviceRequestPriority[priority] || useDict.serviceRequestPriority[normalizedPriority] || priority || "Desconhecida",
        intent: sr.intent || "order",
        categoryLabel,
      });
    });

    setPatients(patientsMap);

    // Agregar por paciente
    const patientAgg = new Map<string, {
      patientId: string;
      patientName: string;
      laboratorial: number;
      imagem: number;
      total: number;
    }>();

    for (const item of serviceRequestsList) {
      const key = item.patientId || "";
      if (!key) continue;
      if (!patientAgg.has(key)) {
        patientAgg.set(key, {
          patientId: item.patientId,
          patientName: item.patientName,
          laboratorial: 0,
          imagem: 0,
          total: 0,
        });
      }
      const agg = patientAgg.get(key)!;
      agg.total += 1;
      if (item.categoryLabel === "Imagem") agg.imagem += 1;
      else agg.laboratorial += 1;
    }

    const patientsSummaryRows = Array.from(patientAgg.values()).sort((a, b) => a.patientName.localeCompare(b.patientName));

    return {
      serviceRequestsByStatus: Array.from(statusMap.entries()).map(([status, count]) => {
        return {
          status: statusTranslation[status] || useDict.serviceRequestStatus[status] || status,
          count,
          percentage: (count / filteredServiceRequests.length) * 100,
        };
      }),
      serviceRequestsByCode: Array.from(codeMap.entries())
        .map(([display, count]) => ({ display, count }))
        .sort((a, b) => b.count - a.count),
      serviceRequestsByPriority: Array.from(priorityMap.entries()).map(([priority, count]) => {
        const normalizedPriority = priority.toLowerCase();
        return {
          priority: useDict.serviceRequestPriority[priority] || useDict.serviceRequestPriority[normalizedPriority] || priority,
          count,
          percentage: (count / filteredServiceRequests.length) * 100,
        };
      }),
      serviceRequestsByCategory: Array.from(categoryMap.entries()).map(([category, count]) => ({
        category,
        count,
        percentage: (count / filteredServiceRequests.length) * 100,
      })),
      totalServiceRequests: filteredServiceRequests.length,
      serviceRequestsList,
      tussServiceRequests: filteredServiceRequests,
      patientsSummaryRows,
    };
  }, [serviceRequestData]);

  // Buscar dados quando os filtros mudarem
  useEffect(() => {
    if (organizationId) {
      fetchServiceRequests();
    }
  }, [organizationId, dateRange, selectedLocation, selectedPractitioner, fetchServiceRequests]);

  // Filtrar exames para a tabela de pesquisa
  const filteredExamsData = useMemo(() => {
    if (!examSearch.trim()) return serviceRequestsByCode;
    const q = examSearch.toLowerCase();
    return serviceRequestsByCode.filter(item => 
      item.display.toLowerCase().includes(q)
    );
  }, [serviceRequestsByCode, examSearch]);

  // Top 5 procedimentos
  const topExamsData = useMemo(() => {
    return serviceRequestsByCode.slice(0, 5);
  }, [serviceRequestsByCode]);

  // Agrupado por paciente com busca
  const filteredPatientsSummary = useMemo(() => {
    if (!search) return patientsSummaryRows;
    return patientsSummaryRows.filter((p) =>
      p.patientName.toLowerCase().includes(search.toLowerCase())
    );
  }, [patientsSummaryRows, search]);

  // Abrir diálogo do paciente
  const handlePatientClick = (patientId: string) => {
    const patient = patients.get(patientId);
    if (patient) {
      setSelectedPatient(patient);
      const patientSRs = serviceRequestsList.filter(sr => sr.patientId === patientId);
      setPatientServiceRequests(patientSRs);
      setPatientDialogOpen(true);
    }
  };

  // Filtrar exames do paciente no diálogo
  const filteredPatientServiceRequests = useMemo(() => {
    if (!dialogSearch) return patientServiceRequests;
    return patientServiceRequests.filter((sr) =>
      sr.display.toLowerCase().includes(dialogSearch.toLowerCase()) ||
      sr.status.toLowerCase().includes(dialogSearch.toLowerCase())
    );
  }, [patientServiceRequests, dialogSearch]);

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Solicitações de Exames"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {/* Abas */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Dados Gerais" />
          <Tab label="Por Paciente" />
        </Tabs>
      </Box>

      {/* Conteúdo das Abas */}
      <TabPanel value={currentTab} index={0}>
        <Stack spacing={5}>
          {/* Gráfico de Status dos Exames */}
          {serviceRequestsByStatus.length > 0 ? (
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                {(() => {
                  const sorted = [...serviceRequestsByStatus].sort((a, b) => b.count - a.count);
                  const total = serviceRequestsByStatus.reduce((acc, item) => acc + item.count, 0);
                  const percentage = total > 0 ? Math.round((sorted[0].count / total) * 100) : 0;
                  return `${percentage}% dos exames estão como '${sorted[0].status}'`;
                })()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                paragraph
                sx={{ fontSize: "0.9rem" }}
              >
                Acompanhe o fluxo de exames. Um número elevado de solicitações
                "ativas" pode indicar um gargalo no processo, desde a coleta
                até a entrega do resultado.
              </Typography>
              <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={serviceRequestsByStatus.map((item) => ({
                      label: item.status,
                      value: item.count,
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis 
                      dataKey="label" 
                      type="category" 
                      width={calculateYAxisWidth(serviceRequestsByStatus.map((item) => ({ label: item.status, value: item.count })))}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value: number | undefined) => {
                        if (value === undefined) return ["0", "Quantidade"];
                        const total = serviceRequestsByStatus.reduce((acc, item) => acc + item.count, 0);
                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                        return [`${value} (${percentage}%)`, 'Quantidade'];
                      }}
                    />
                    <Bar dataKey="value" name="Quantidade">
                      {serviceRequestsByStatus.map((item, index) => {
                        const maxValue = Math.max(...serviceRequestsByStatus.map(d => d.count));
                        const isHighlight = item.count === maxValue;
                        return (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={isHighlight ? theme.palette.primary.main : '#9E9E9E'} 
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <NoData message="Sem dados de status de exames." />
          )}

          {/* Gráfico de Top 5 Procedimentos */}
          {serviceRequestsByCode.length > 0 && <Divider />}

          {serviceRequestsByCode.length > 0 ? (
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                {(() => {
                  const total = serviceRequestsByCode.reduce((acc, item) => acc + item.count, 0);
                  const percentage = total > 0 ? Math.round((serviceRequestsByCode[0].count / total) * 100) : 0;
                  return `'${serviceRequestsByCode[0].display}' representa ${percentage}% dos exames solicitados`;
                })()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                paragraph
                sx={{ fontSize: "0.9rem" }}
              >
                Veja os 5 exames mais solicitados. Use a tabela abaixo para
                pesquisar todos os exames disponíveis.
              </Typography>
              <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={serviceRequestsByCode.slice(0, 5).map((item) => ({
                      label: item.display,
                      value: item.count,
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis 
                      dataKey="label" 
                      type="category" 
                      width={calculateYAxisWidth(serviceRequestsByCode.slice(0, 5).map((item) => ({ label: item.display, value: item.count })))}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value: number | undefined) => {
                        if (value === undefined) return ["0", "Quantidade"];
                        const total = serviceRequestsByCode.reduce((acc, item) => acc + item.count, 0);
                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                        return [`${value} (${percentage}%)`, 'Quantidade'];
                      }}
                    />
                    <Bar dataKey="value" name="Quantidade">
                      {serviceRequestsByCode.slice(0, 5).map((item, index) => {
                        const maxValue = Math.max(...serviceRequestsByCode.slice(0, 5).map(d => d.count));
                        const isHighlight = item.count === maxValue;
                        return (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={isHighlight ? theme.palette.primary.main : '#9E9E9E'} 
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Tabela de Todos os Exames */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Todos os Exames ({serviceRequestsByCode.length})
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Buscar exame..."
                  value={examSearch}
                  onChange={(e) => setExamSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <GenericTable
                  tableRows={filteredExamsData.map((item) => ({
                    display: item.display,
                    count: item.count,
                    percentage:
                      totalServiceRequests > 0
                        ? `${((item.count / totalServiceRequests) * 100).toFixed(1)}%`
                        : "0%",
                  }))}
                  tableColumns={[
                    { column: "Exame", rowName: "display", align: "left" },
                    { column: "Quantidade", rowName: "count", width: "140px" },
                    { column: "Percentual", rowName: "percentage", width: "140px" },
                  ]}
                  characterLimit={80}
                />
              </Box>
            </Box>
          ) : (
            <NoData message="Sem dados de exames." />
          )}

        </Stack>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
            <Stack spacing={2}>
              {/* Busca */}
              <TextField
                fullWidth
                placeholder="Buscar paciente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Tabela agregada por paciente (Laboratorial vs Imagem) */}
              {loadingServiceRequests ? (
                <Skeleton variant="rectangular" height={400} />
              ) : (
                <GenericTable
                  tableRows={filteredPatientsSummary}
                  tableColumns={[
                    { column: "Paciente", rowName: "patientName", align: "left" },
                    { column: "Laboratorial", rowName: "laboratorial", width: "140px" },
                    { column: "Imagem", rowName: "imagem", width: "120px" },
                    { column: "Total", rowName: "total", width: "100px" },
                  ]}
                  characterLimit={60}
                  onRowClick={(row) => handlePatientClick(row.patientId)}
                />
              )}
            </Stack>
      </TabPanel>

      {/* Diálogo do Paciente */}
      <Dialog
          open={patientDialogOpen}
          onClose={() => setPatientDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Exames do Paciente: {selectedPatient?.name?.[0]?.text}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                fullWidth
                placeholder="Buscar exames..."
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <GenericTable
                tableRows={filteredPatientServiceRequests}
                tableColumns={[
                  { column: "Procedimento", rowName: "display", align: "left" },
                  { column: "Categoria", rowName: "categoryLabel", width: "140px" },
                  { column: "Status", rowName: "statusLabel", width: "140px" },
                  { column: "Prioridade", rowName: "priorityLabel", width: "140px" },
                  { column: "Data", rowName: "authoredOn", width: "120px" },
                ]}
                characterLimit={80}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPatientDialogOpen(false)}>
              Fechar
            </Button>
          </DialogActions>
      </Dialog>
    </Stack>
  );
}
